import React, { useEffect, useState } from "react";
import { Wallet, Route, Clock, UtensilsCrossed, Receipt, Bike, Car, Star, Package, UserX } from "lucide-react";
import { TrustBadge } from "../../components/app/TipAndTrust";
import { toast } from "sonner";
import { PageHeader, Section, StatCard, LoadingBlock, EmptyState } from "../../components/app/ui";
import ReportFilters, { defaultFilter, toParams } from "../../components/app/ReportFilters";
import { driverApi, inr2, formatDate, errorMessage } from "../../lib/api";

export function BarChart({ data, valueKey = "total", labelKey = "date", color = "#1D4ED8", format = inr }) {
  const max = Math.max(1, ...data.map((d) => Number(d[valueKey]) || 0));
  return (
    <div className="flex h-48 items-end gap-2" data-testid="bar-chart">
      {data.map((d) => {
        const v = Number(d[valueKey]) || 0;
        const h = Math.max(4, (v / max) * 100);
        return (
          <div key={d[labelKey]} className="group flex flex-1 flex-col items-center justify-end gap-2">
            <span className="text-[11px] font-semibold text-ink opacity-0 transition-opacity group-hover:opacity-100">{format(v)}</span>
            <div className="w-full rounded-t-lg transition-colors" style={{ height: `${h}%`, background: v ? color : "#E5E7EB" }} title={`${d[labelKey]}: ${format(v)}`} />
            <span className="text-[10px] text-ink-muted">{new Date(d[labelKey]).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function Earnings() {
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState(defaultFilter);
  const [report, setReport] = useState(null);
  const [statements, setStatements] = useState(null);
  const [goodsRatings, setGoodsRatings] = useState(null);

  useEffect(() => { driverApi.earningsSummary().then(setSummary).catch((e) => { toast.error(errorMessage(e)); setSummary({}); }); }, []);
  useEffect(() => { setReport(null); driverApi.earningsReport(toParams(filter)).then(setReport).catch(() => setReport({ summary: {}, by_day: [], transactions: [] })); }, [filter]);
  useEffect(() => { driverApi.payoutStatements().then(setStatements).catch(() => setStatements([])); }, []);
  useEffect(() => { driverApi.goodsRatings().then(setGoodsRatings).catch(() => setGoodsRatings(null)); }, []);

  if (!summary) return <><PageHeader title="Earnings" /><LoadingBlock rows={3} /></>;
  const { today = {}, week = {}, month = {} } = summary;
  const rs = report?.summary || {};
  const tx = report?.transactions || [];

  return (
    <>
      <PageHeader title="Earnings" description="Rides, food & goods deliveries, updated after every completed trip." />
      <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Today" value={inr2(today.total || 0)} hint={`${today.trips || 0} trips`} icon={Wallet} />
        <StatCard label="This week" value={inr2(week.total || 0)} hint={`${week.trips || 0} trips`} icon={Route} tone="graphite" />
        <StatCard label="This month" value={inr2(month.total || 0)} hint={`${month.trips || 0} trips`} icon={Wallet} tone="ink" />
        <StatCard label="Hours online today" value={`${(summary.hours_online_today || 0).toFixed(1)} h`} icon={Clock} />
      </div>

      {goodsRatings && goodsRatings.count > 0 && (
        <Section title="Goods delivery rating" description="How receivers rate your deliveries — use their notes to improve.">
          {goodsRatings.trust?.eligible ? <div className="mb-3" data-testid="driver-trust"><TrustBadge trust={goodsRatings.trust} /></div> : null}
          <div className="grid gap-5 lg:grid-cols-[auto_1fr]" data-testid="driver-goods-ratings">
            <div className="flex flex-col items-center justify-center rounded-2xl bg-brand-50 px-6 py-4">
              <p className="flex items-center gap-1 text-3xl font-black text-brand" data-testid="goods-rating-avg"><Star className="h-6 w-6 fill-brand" /> {goodsRatings.average.toFixed(2)}</p>
              <p className="text-xs font-semibold text-ink-soft">{goodsRatings.count} rated {goodsRatings.count === 1 ? "delivery" : "deliveries"}</p>
            </div>
            <div className="space-y-1.5">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = goodsRatings.breakdown?.[String(n)] || 0;
                const pct = goodsRatings.count ? Math.round((c / goodsRatings.count) * 100) : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-xs" data-testid={`goods-rating-bar-${n}`}>
                    <span className="flex w-8 items-center gap-0.5 font-semibold text-ink-soft">{n} <Star className="h-3 w-3 fill-amber-400 text-amber-400" /></span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-200"><div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} /></div>
                    <span className="w-8 text-right text-ink-muted">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {goodsRatings.recent?.length > 0 && (
            <div className="mt-5 border-t border-surface-200 pt-4">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Package className="h-3.5 w-3.5" /> Recent notes</p>
              <div className="space-y-2">
                {goodsRatings.recent.slice(0, 6).map((r, i) => (
                  <div key={i} className="rounded-xl bg-surface-50 px-3 py-2 text-sm" data-testid={`goods-review-${i}`}>
                    <p className="flex items-center gap-1 text-xs font-semibold text-amber-600">{"★".repeat(r.rating)}<span className="text-ink-muted">{"★".repeat(5 - r.rating)}</span>{r.order_number ? <span className="ml-2 font-mono text-[10px] text-ink-muted">{r.order_number}</span> : null}</p>
                    <p className="text-ink-soft">{r.review}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {goodsRatings.receiver?.count > 0 && (
            <div className="mt-4 rounded-xl bg-surface-50 p-3" data-testid="driver-receiver-feedback">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-ink-soft">Receiver feedback · <span className="flex items-center gap-0.5 text-amber-600"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {goodsRatings.receiver.average.toFixed(2)}</span> <span className="text-ink-muted">({goodsRatings.receiver.count}) · separate from your average</span></p>
              {goodsRatings.receiver.recent?.slice(0, 4).map((r, i) => (
                <div key={i} className="mt-1.5 text-sm" data-testid={`receiver-review-${i}`}>
                  <span className="text-amber-600">{"★".repeat(r.rating)}</span><span className="text-surface-300">{"★".repeat(5 - r.rating)}</span>
                  {r.review ? <span className="ml-2 text-ink-soft">{r.review}</span> : null}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-ink-muted">Reports & history</h2>
      <p className="mb-4 text-sm text-ink-soft">Filter your earnings and trip history by date range and type{report?.window?.label ? ` · ${report.window.label}` : ""}.</p>
      <ReportFilters value={filter} onChange={setFilter} showType />

      {report === null && <LoadingBlock rows={3} />}
      {report && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Earnings" value={inr2(rs.gross || 0)} hint={`${rs.trips || 0} trips`} icon={Wallet} tone="brand" />
            <StatCard label="Platform fees" value={inr2(rs.fees || 0)} hint={rs.goods_commission > 0 ? `incl. ${inr2(rs.goods_commission)} goods commission` : "Ride & goods commission"} icon={Receipt} />
            <StatCard label="Net payout" value={inr2(rs.net_payout || 0)} hint="Paid to you" icon={Wallet} tone="graphite" />
            <StatCard label="Trips" value={rs.trips || 0} hint={`${rs.ride_trips || 0} rides · ${rs.food_trips || 0} food · ${rs.goods_trips || 0} goods`} icon={Route} tone="ink" />
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
            <Section title="Daily earnings" description={report.window?.label}>
              {(report.by_day || []).length ? <BarChart data={report.by_day} /> : <p className="text-sm text-ink-muted">No earnings in this period.</p>}
              <div className="mt-6 grid grid-cols-2 gap-4 border-t border-surface-200 pt-5 text-sm">
                <div><p className="inline-flex items-center gap-1 text-ink-soft"><Car className="h-3.5 w-3.5" /> Rides</p><p className="mt-1 text-lg font-bold text-ink">{inr2(rs.ride_amount || 0)}</p></div>
                <div><p className="inline-flex items-center gap-1 text-ink-soft"><UtensilsCrossed className="h-3.5 w-3.5" /> Food deliveries</p><p className="mt-1 text-lg font-bold text-ink">{inr2(rs.food_amount || 0)}</p></div>
                <div><p className="inline-flex items-center gap-1 text-ink-soft"><Package className="h-3.5 w-3.5" /> Goods delivery</p><p className="mt-1 text-lg font-bold text-ink">{inr2(rs.goods_amount || 0)}</p></div>
                {(rs.ride_commission > 0 || rs.goods_commission > 0) && (
                  <div className="col-span-2 grid grid-cols-2 gap-4 border-t border-surface-200 pt-4" data-testid="commission-breakdown">
                    <div><p className="inline-flex items-center gap-1 text-ink-soft"><Receipt className="h-3.5 w-3.5" /> Taxi commission</p><p className="mt-1 text-lg font-bold text-amber-700">−{inr2(rs.ride_commission || 0)}</p></div>
                    <div data-testid="goods-commission"><p className="inline-flex items-center gap-1 text-ink-soft"><Package className="h-3.5 w-3.5" /> Goods commission</p><p className="mt-1 text-lg font-bold text-amber-700">−{inr2(rs.goods_commission || 0)}</p></div>
                  </div>
                )}
                {rs.no_show_count > 0 && (
                  <div className="col-span-2 rounded-xl bg-amber-50 p-3" data-testid="no-show-stat">
                    <p className="inline-flex items-center gap-1 text-amber-700"><UserX className="h-3.5 w-3.5" /> No-show fees <span className="text-xs font-normal text-amber-600">· not completed trips</span></p>
                    <p className="mt-1 text-lg font-bold text-amber-800">{inr2(rs.no_show_amount || 0)} <span className="text-xs font-semibold text-amber-600">· {rs.no_show_count} this period</span></p>
                  </div>
                )}
              </div>
            </Section>
            <Section title="Trip history" description={`${tx.length} shown`}>
              {tx.length === 0 ? <EmptyState icon={Bike} title="No trips" text="Completed trips in this period will be listed here." /> : (
                <ul className="divide-y divide-surface-200 text-sm" data-testid="trip-history">
                  {tx.map((r, i) => (
                    <li key={r.earning_id || i} className="flex items-center justify-between py-3" data-testid={`trip-${r.earning_id || i}`}>
                      <div>
                        <p className="inline-flex items-center gap-1.5 font-medium text-ink">{r.source === "no_show" ? <><UserX className="h-3.5 w-3.5 text-amber-600" /> No-show fee</> : r.kind === "food" ? <><UtensilsCrossed className="h-3.5 w-3.5 text-brand" /> Food delivery</> : r.kind === "goods" ? <><Package className="h-3.5 w-3.5 text-amber-600" /> Goods delivery</> : <><Car className="h-3.5 w-3.5 text-graphite" /> Ride</>}{r.source !== "no_show" && r.distance_km ? ` · ${r.distance_km} km` : ""}{r.source === "no_show" ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">No completed trip</span> : null}</p>
                        <p className="text-xs text-ink-muted">{formatDate(r.created_at)}</p>
                      </div>
                      <span className="font-semibold text-ink">{inr2(r.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        </>
      )}

      <Section title="Weekly payout statements" description="Your delivery fees and ride earnings, week by week. Cash you collected on COD orders is netted off." className="mt-8">
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl bg-surface-100 px-4 py-3 text-xs text-ink-soft">
          <Receipt className="h-4 w-4 text-brand" />
          <span className="flex-1"><strong className="text-ink">Net payable</strong> = your earnings minus the cash you kept from Cash-on-Delivery orders and cash rides. A negative figure means you are holding our cash and owe the difference.</span>
          <button onClick={() => driverApi.downloadStatements(statements?.length || 8).catch((e) => toast.error(errorMessage(e)))} disabled={!statements || !statements.length} className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-50" data-testid="download-statements-pdf"><Receipt className="h-4 w-4" /> Download PDF</button>
        </div>
        {statements === null ? <LoadingBlock rows={2} /> : statements.length === 0 ? (
          <EmptyState icon={Receipt} title="No statements yet" text="Weekly statements appear once you complete trips." />
        ) : (
          <div className="-mx-2 overflow-x-auto">
            <table className="w-full min-w-[880px] border-collapse text-sm" data-testid="payout-statements">
              <thead>
                <tr className="border-b border-surface-300 text-left text-xs uppercase tracking-wider text-ink-muted">
                  <th className="px-2 py-2 font-semibold">Week</th>
                  <th className="px-2 py-2 text-right font-semibold">Deliveries</th>
                  <th className="px-2 py-2 text-right font-semibold">Rides</th>
                  <th className="px-2 py-2 text-right font-semibold">Goods</th>
                  <th className="px-2 py-2 text-right font-semibold">Earned</th>
                  <th className="px-2 py-2 text-right font-semibold">Cash kept</th>
                  <th className="px-2 py-2 text-right font-semibold">Goods fee</th>
                  <th className="px-2 py-2 text-right font-semibold">Cancel fees</th>
                  <th className="px-2 py-2 text-right font-semibold">Net payable</th>
                </tr>
              </thead>
              <tbody>
                {statements.map((s) => (
                  <tr key={s.week_start} className="border-b border-surface-200 last:border-0" data-testid={`statement-${s.week_start}`}>
                    <td className="px-2 py-2 font-medium text-ink">{s.label}{s.current ? <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand">This week</span> : ""}</td>
                    <td className="px-2 py-2 text-right text-ink-soft">{s.deliveries}</td>
                    <td className="px-2 py-2 text-right text-ink-soft">{s.rides}</td>
                    <td className="px-2 py-2 text-right text-ink-soft">{s.goods ?? 0}</td>
                    <td className="px-2 py-2 text-right text-ink-soft">{inr2(s.total)}</td>
                    <td className="px-2 py-2 text-right text-ink-soft">{inr2(s.cash_collected)}</td>
                    <td className="px-2 py-2 text-right text-ink-soft" data-testid={`statement-goods-fee-${s.week_start}`}>{inr2(s.goods_fee || 0)}</td>
                    <td className="px-2 py-2 text-right text-emerald-700" data-testid={`statement-cancel-fees-${s.week_start}`}>{s.cancellation_fees ? `+${inr2(s.cancellation_fees)}` : inr2(0)}</td>
                    <td className={`px-2 py-2 text-right font-bold ${s.net_payable < 0 ? "text-red-600" : "text-ink"}`} data-testid={`statement-net-${s.week_start}`}>{inr2(s.net_payable)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  );
}
